/* eslint-disable security/detect-object-injection */
/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */
/* Custom Data Action to get sales lines after Search order API returns result because
    Search order API does not return sales lines data
*/

import * as Msdyn365 from '@msdyn365-commerce/core';
import { SalesOrder } from '@msdyn365-commerce/retail-proxy';
import {
    searchOrdersAsync,
    getSalesOrderDetailsBySalesIdAsync,
    getSalesOrderDetailsByTransactionIdAsync
} from '@msdyn365-commerce/retail-proxy/dist/DataActions/SalesOrdersDataActions.g';
import { buildCacheKey } from '@msdyn365-commerce-modules/retail-actions';

interface ISearchOrderHistoryCriteria {
    key: string;
    searchTerm: string;
    accountNumber?: string;
}
/**
 * SearchOrderHistory Input Action
 */
export class SearchOrderHistoryInput implements Msdyn365.IActionInput {
    public searchCriteria: ISearchOrderHistoryCriteria;
    private readonly apiSettings: Msdyn365.ICommerceApiSettings;
    // TODO: Construct the input needed to run the action
    constructor(searchCriteria: ISearchOrderHistoryCriteria, apiSettings: Msdyn365.ICommerceApiSettings) {
        this.searchCriteria = searchCriteria;
        this.apiSettings = apiSettings;
    }

    // TODO: Determine if the results of this get action should cache the results and if so provide
    // a cache object type and an appropriate cache key
    public getCacheKey = () => buildCacheKey('SearchOrderHistory', this.apiSettings);
    public getCacheObjectType = () => 'SearchOrderHistory';
    public dataCacheType = (): Msdyn365.CacheType => 'application';
}

// TODO: Create a data model here or import one to capture the response of the action
export interface ISearchOrderHistoryData {
    salesOrders: SalesOrder[];
}

/**
 * TODO: Use this function to call your action and process the results as needed
 */
export async function SearchOrderHistoryAction(
    input: SearchOrderHistoryInput,
    ctx: Msdyn365.IActionContext
): Promise<ISearchOrderHistoryData> {
    // const apiSettings = Msdyn365.msdyn365Commerce.apiSettings;
    try {
        const context = { callerContext: ctx };
        const {
            searchCriteria: { key, searchTerm, accountNumber }
        } = input;
        const query = {
            CustomerAccountNumber: accountNumber
        };
        /* if order status option is not selected then perform normal query
            else fetch all orders then filter on client side
        */
        if (key !== 'OrderStatusValues') {
            query[key] = searchTerm;
        }
        const response = await searchOrdersAsync(context, query);
        let salesOrders: SalesOrder[] = await Promise.all(
            response.map(async salesOrder => {
                let order = null;
                // get sales lines for each order because SearchOrders API only provides data for order header
                if (salesOrder.SalesId) {
                    order = await getSalesOrderDetailsBySalesIdAsync(context, salesOrder.SalesId);
                } else {
                    order = await getSalesOrderDetailsByTransactionIdAsync(context, salesOrder.Id, 0);
                }
                return {
                    ...salesOrder,
                    SalesLines: order.SalesLines
                };
            })
        );
        // Filter orders based on order status ( 4 = shipped, 7 = cancelled )
        if (key === 'OrderStatusValues') {
            switch (searchTerm) {
                case '4':
                    salesOrders = salesOrders.filter(order => {
                        const salesLine = order.SalesLines && order.SalesLines.length > 0 ? order.SalesLines[0] : null;
                        return salesLine?.SalesStatusValue === 4;
                    });
                    break;

                case '7':
                    salesOrders = salesOrders.filter(order => {
                        const salesLine = order.SalesLines && order.SalesLines.length > 0 ? order.SalesLines[0] : null;
                        return salesLine?.SalesStatusValue === 7;
                    });
                    break;

                default:
                    salesOrders = salesOrders.filter(order => {
                        const salesLine = order.SalesLines && order.SalesLines.length > 0 ? order.SalesLines[0] : null;
                        return salesLine?.SalesStatusValue !== 7 && salesLine?.SalesStatusValue !== 4;
                    });
            }
        }
        return { salesOrders: salesOrders };
    } catch (error) {
        return { salesOrders: [] };
    }
    // TODO: Uncomment the below line to get the value from a service
    // const response = await Msdyn365.sendRequest<ISearchOrderHistoryData[]>('/get/example/id/1', 'get');
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<ISearchOrderHistoryData>>SearchOrderHistoryAction,
    // TODO: Give your data action a unique id
    id: 'SearchOrderHistory'
    // TODO: Uncomment the below line if this is a meant to be a batched data action
    // isBatched: true
});
